import type {DiffLine} from '@github-ui/diff-lines/types'
import type {DiffLineType} from '@github-ui/diffs/types'

export type MakeChangeGroupsOrchestrationState =
  | 'CREATED'
  | 'STARTED'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'SKIPPED'
  | 'WAITING'
  | 'ABANDONED'

/** Represents a line in a group hunk that will be used to match with a corresponding diff line in a diff entry */
export interface GroupHunkLine {
  /** The line type (e.g. "CONTEXT", "ADDITION", or "DELETION") that will safely allow a correct match with a diff line in a diff entry. */
  type: DiffLineType
  /** (right side for "CONTEXT" and "ADDITION" types and left side for "DELETION" type) */
  line: number
}

export const changeGroupGenerationUnavailableReasons = {
  diffTooLarge: 'DIFF_TOO_LARGE',
  diffTooSmall: 'DIFF_TOO_SMALL',
  tooFewGroups: 'TOO_FEW_GROUPS',
  tooManyGroups: 'TOO_MANY_GROUPS',
  unableToGenerate: 'UNABLE_TO_GENERATE',
} as const

export type ChangeGroupGenerationUnavailableReasons =
  (typeof changeGroupGenerationUnavailableReasons)[keyof typeof changeGroupGenerationUnavailableReasons]

/**
 * Represents a hunk position within a group that can be used to filter a hunk from a collection of diff lines in a diff entry.
 */
interface GroupHunk {
  /** A fallback hunk header diff line that will be used when filtering diff lines from a diff entry */
  headerLine: DiffLine
  /** The first diff line's type and line number for a diff hunk. */
  startLine: GroupHunkLine
  /** The last diff line's type and line number for a diff hunk. */
  endLine: GroupHunkLine
}

/**
 * Represents a group that can be used to filter files in the file tree.
 */
export interface Group {
  /** Unique identifier for the group */
  id: number
  /** Display name of the group */
  name: string
  /** Detailed description of what the group represents */
  description: string
  /** Priority value used for sorting groups (lower values appear first) */
  priority: number
  /** Hunk positions stored by diff entry paths used for filtering hunks to render in a diff entry by group */
  hunkPositions: Record<string, GroupHunk[]>
}

export interface ChangeGroupOrchestration {
  /** Unique identifier for the last change group orchestration */
  id: number
  /** The current state of the orchestration */
  state: MakeChangeGroupsOrchestrationState
}

export interface ChangeGroupPayload {
  canAccessChangeGroups: boolean
  changeGroups: Group[]
  latestChangeGroupOrchestration: ChangeGroupOrchestration | null
  changeGroupGenerationChannel: string
  unavailableReason: ChangeGroupGenerationUnavailableReasons | null
}
